<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { RelationshipPill, AvailableGroup } from './types';

const props = defineProps<{
  isPublic: boolean;
  relationships: RelationshipPill[];
  availableGroups: AvailableGroup[];
  canManage: boolean;
}>();

const emit = defineEmits<{
  (e: 'toggle-public', makePublic: boolean): void;
  (e: 'save-relationships', groupIds: number[]): void;
}>();

const showAdvanced = ref(false);
const saving = ref(false);
const togglingPublic = ref(false);

const currentGroupIds = computed(() => new Set(props.relationships.map((r) => r.id)));

// Local selection for the "Change Project relationship" checkbox list.
const selected = ref<Set<number>>(new Set());

function resetSelection() {
  selected.value = new Set(
    props.availableGroups.filter((g) => currentGroupIds.value.has(g.id)).map((g) => g.id),
  );
}

// Keep selection in sync whenever the underlying relationships change.
watch(() => props.relationships, resetSelection, { immediate: true });

function toggleGroup(id: number) {
  const next = new Set(selected.value);
  next.has(id) ? next.delete(id) : next.add(id);
  selected.value = next;
}

async function onTogglePublic(event: Event) {
  const makePublic = (event.target as HTMLInputElement).checked;
  togglingPublic.value = true;
  emit('toggle-public', makePublic);
  // Parent reloads data; release the local guard shortly after.
  setTimeout(() => (togglingPublic.value = false), 600);
}

function openAdvanced() {
  resetSelection();
  showAdvanced.value = true;
}

function cancelAdvanced() {
  resetSelection();
  showAdvanced.value = false;
}

function save() {
  saving.value = true;
  emit('save-relationships', Array.from(selected.value));
  setTimeout(() => {
    saving.value = false;
    showAdvanced.value = false;
  }, 600);
}
</script>

<template>
  <section class="pp-section pp-public">
    <div class="pp-section-title">Is this page Public?</div>

    <div class="d-flex align-items-center gap-2 flex-wrap">
      <div class="form-check m-0">
        <input
          id="pp-public-toggle"
          class="form-check-input"
          type="checkbox"
          :checked="isPublic"
          :disabled="!canManage || togglingPublic"
          @change="onTogglePublic"
        />
        <label class="form-check-label" for="pp-public-toggle">
          {{ isPublic ? 'Public' : 'Not Public' }}
        </label>
      </div>

      <button
        v-if="canManage"
        type="button"
        class="btn btn-sm btn-outline-primary"
        @click="showAdvanced ? cancelAdvanced() : openAdvanced()"
      >
        Change Project relationship
      </button>
    </div>

    <!-- Advanced: per-project checkbox list -->
    <div v-if="showAdvanced" class="pp-advanced card mt-3">
      <div class="card-body">
        <p class="mb-2 small">
          First Project of the list is the "public" project. When ticked content is public.
        </p>

        <div
          v-for="group in availableGroups"
          :key="group.id"
          class="form-check"
          :class="{ 'pp-default-group': group.is_default }"
        >
          <input
            :id="`pp-group-${group.id}`"
            class="form-check-input"
            type="checkbox"
            :checked="selected.has(group.id)"
            @change="toggleGroup(group.id)"
          />
          <label class="form-check-label" :for="`pp-group-${group.id}`">
            {{ group.label }}
          </label>
        </div>

        <div class="d-flex gap-2 mt-3">
          <button type="button" class="btn btn-primary flex-grow-1" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Save
          </button>
          <button type="button" class="btn btn-secondary" :disabled="saving" @click="cancelAdvanced">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.pp-advanced {
  background: #f8f9fa;
}
.pp-default-group .form-check-label {
  color: #dc3545;
  font-weight: 600;
}
</style>
